import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { theme } from '../../../theme/theme'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { BodyMedium, Label, Title4 } from '../../../components/styles/Typography'
import { useSelectUserOrganisationName } from '../../../state/selectors'
import { Header } from '../../../components/Header'
import { Download } from '../../../components/icons/Download'

const handleDownload = () => {
  const docUrl = 'https://ester-public-assets.s3.eu-north-1.amazonaws.com/policy-template-240923.docx'
  window.open(docUrl, '_blank')
}

export const PolicyAdmin = () => {
  const { t } = useTranslation()
  const organisationName = useSelectUserOrganisationName()

  return (
    <>
      <Header
        title={t('admin.policy.title')}
        description={t('admin.policy.description')}
        backgroundColor={theme.color.beigeLight}
        backButtonLink="/admin/services"
      />
      <ComponentLayout paddingTop={0}>
        <Container>
          <PolicyHeader>
            <Label>{organisationName}</Label>
            <StyledLink onClick={handleDownload}>
              <Download color={theme.color.plumMid} size={16} />
              <Label>{t('admin.policy.downloadLabel')}</Label>
            </StyledLink>
          </PolicyHeader>
          <StyledTitle4>{t('admin.policy.backgroundTitle')}</StyledTitle4>
          <StyledBodyMedium>{t('admin.policy.backgroundParagraph1')}</StyledBodyMedium>
          <StyledBodyMedium>{t('admin.policy.backgroundParagraph2')}</StyledBodyMedium>
          <StyledBodyMedium>{t('admin.policy.backgroundParagraph3')}</StyledBodyMedium>
          <StyledBodyMedium>{t('admin.policy.backgroundParagraph4')}</StyledBodyMedium>
          <StyledBodyMedium>{t('admin.policy.backgroundParagraph5')}</StyledBodyMedium>
          <StyledBodyMedium>{t('admin.policy.principlesParagraph1')}</StyledBodyMedium>
          <StyledBodyMedium>{t('admin.policy.principlesParagraph7')}</StyledBodyMedium>
          <LargeSpacer />
          <StyledTitle4>{t('admin.policy.actionsTitle')}</StyledTitle4>
          <StyledBodyMedium>{t('admin.policy.actionsIntro')}</StyledBodyMedium>
          <InlineHeader>{t('admin.policy.educationSubtitle')}</InlineHeader>
          <StyledBodyMedium>
            <ul>
              <li>{t('admin.policy.educationPoint1')}</li>
              <li>{t('admin.policy.educationPoint2')}</li>
              <li>{t('admin.policy.educationPoint3')}</li>
              <li>{t('admin.policy.educationPoint4')}</li>
              <li>{t('admin.policy.educationPoint5')}</li>
            </ul>
          </StyledBodyMedium>
          <InlineHeader>{t('admin.policy.preventiveSubtitle')}</InlineHeader>
          <StyledBodyMedium>
            <ul>
              <li>{t('admin.policy.preventivePoint1')}</li>
              <li>{t('admin.policy.preventivePoint2')}</li>
              <li>{t('admin.policy.preventivePoint3')}</li>
            </ul>
          </StyledBodyMedium>
          <InlineHeader>{t('admin.policy.workEnvironmentSubtitle')}</InlineHeader>
          <StyledBodyMedium>
            <ul>
              <li>{t('admin.policy.workEnvironmentPoint1')}</li>
              <li>{t('admin.policy.workEnvironmentPoint2')}</li>
              <li>{t('admin.policy.workEnvironmentPoint3')}</li>
            </ul>
          </StyledBodyMedium>
          <LargeSpacer />
          <StyledTitle4>{t('admin.policy.principlesTitle')}</StyledTitle4>
          <StyledBodyMedium>
            <Emphasis>{t('admin.policy.principleRespect')}: </Emphasis>
            {t('admin.policy.principleRespectText')}
          </StyledBodyMedium>
          <StyledBodyMedium>
            <Emphasis>{t('admin.policy.principleNonDiscrimination')}: </Emphasis>
            {t('admin.policy.principleNonDiscriminationText')}
          </StyledBodyMedium>
          <StyledBodyMedium>
            <Emphasis>{t('admin.policy.principleInclusive')}: </Emphasis>
            {t('admin.policy.principleInclusiveText')}
          </StyledBodyMedium>
          <LargeSpacer />
          <StyledTitle4>{t('admin.policy.responsibilityTitle')}</StyledTitle4>
          <StyledBodyMedium>{t('admin.policy.responsibilityParagraph1')}</StyledBodyMedium>
          <StyledBodyMedium>
            <ul>
              <li>
                <Emphasis>{t('admin.policy.responsibilityManagement')} </Emphasis>
                {t('admin.policy.responsibilityManagementText')}
              </li>
              <li>
                <Emphasis>{t('admin.policy.responsibilityHR')} </Emphasis>
                {t('admin.policy.responsibilityHRText')}
              </li>
              <li>
                <Emphasis>{t('admin.policy.responsibilityManagers')} </Emphasis>
                {t('admin.policy.responsibilityManagersText')}
              </li>
              <li>
                <Emphasis>{t('admin.policy.responsibilityEmployees')} </Emphasis>
                {t('admin.policy.responsibilityEmployeesText')}
              </li>
            </ul>
          </StyledBodyMedium>
        </Container>
      </ComponentLayout>
    </>
  )
}

const PolicyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.large}px;
`
const StyledLink = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${theme.color.plumMid};
`

const Container = styled.div`
  padding: ${theme.spacing.xlarge}px;
  max-width: 712px;
  margin: 0;
  margin-bottom: ${theme.spacing.xxlarge}px;

  border: 1px solid ${theme.color.beigeDark};
  border-radius: ${theme.spacing.medium}px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.06),
    0px 3px 9px 0px rgba(0, 0, 0, 0.05);
  background: ${theme.color.white};

  @media screen and (max-width: ${theme.breakpoint.small}px) {
    background: none;
    padding: 0;
    border: none;
    box-shadow: none;
  }
`
const StyledTitle4 = styled(Title4)`
  margin-bottom: ${theme.spacing.xsmall}px;
`
const LargeSpacer = styled.div`
  height: ${theme.spacing.large}px;
`
const StyledBodyMedium = styled(BodyMedium)`
  margin-bottom: ${theme.spacing.xsmall}px;
`
const Emphasis = styled.span`
  font-weight: 500;
`
const InlineHeader = styled(StyledBodyMedium)`
  font-weight: 500;
  margin-top: ${theme.spacing.medium}px;
`
